import type { WalletType } from '@/types/WalletType';
import type Destinatary from '@/types/DestinataryType';
import { STABLECOIN_COUNTRY_CODE } from '@/shared/common/constants/stablecoinCountry.constant';
import {
  BANK_NAME_ICON_MAP,
  STABLECOIN_CURRENCY_ICON_MAP,
} from '@/shared/common/constants/stablecoinIcons.constant';
import { formatCurrency } from '@/helpers/formatCurrency';
import { getAsset } from '@/helpers/assets';
import { accountTypesData } from '@/shared/common/constants/accountTypes.constant';
import { getRateBreakdown } from '@/shared/common/utils/rateBreakdown.util';
import { textFeeQuoter } from '@/shared/common/utils/feeText.util';
import { maskWalletAddress } from '@/shared/common/utils/maskWalletAddress.util';
import { checkIfBeneficiaryHasSwiftRoute } from '@/helpers/swift';
import type { IRemittanceDetailResponse, TransactionCostList } from '@/shared/common/types/remittance.type';
import type { NewTypeMovement } from '@/shared/common/types/movement.type';
import type { StatusDb } from '@/shared/common/types/status.type';
import type { MovementModalDetailsType, TooltipDetailType } from '@/shared/common/types/movements-detail/transactionModalDetail.type';
import { PIX_TYPES, STATUS_LIST } from '../constants/transactionModal.constant';

export function getFirstCode(numerals: string): string {
  return numerals.split(',')[0].trim();
}

export function getAccountNameType(countryCode: string, type: string): string | undefined {
  const accountTypes = accountTypesData[countryCode || 'default'];
  if (accountTypes) {
    return accountTypes.find((account) => account.value === type)?.text;
  }
  return accountTypesData.default.find((account) => account.value === type)?.text;
}

export function getPixType(destinatary: Destinatary | undefined): string {
  return (
    PIX_TYPES.find((item) => item.value === destinatary?.routingCodeType1)?.label ??
    'Clave aleatoria'
  );
}

export function createStringOperationalCost(
  taxes: TransactionCostList[],
  amount: number,
  currency: string
): string {
  const operationalCosts = taxes.reduce((acc, tax) => {
    if (!tax.isRefundable) {
      return acc + tax.amount;
    }
    return acc;
  }, 0);
  const totalAmount = amount - operationalCosts;
  return `Te devolvimos [${formatCurrency(totalAmount, currency)}] a tu cuenta Global66. Hemos tenido que descontar [${formatCurrency(operationalCosts, currency)}] por concepto de costos operativos.`;
}

export function buildTooltipDetail(
  transactionData: IRemittanceDetailResponse,
  taxes: TransactionCostList[]
): Record<'tooltipDetail', TooltipDetailType> {
  const description = createStringOperationalCost(
    taxes,
    transactionData.originAmount,
    transactionData.originCurrency
  );
  return {
    tooltipDetail: {
      decription: description,
      icon: 'regular info-circle',
    },
  };
}

export function movementModalDetails(
  transactionData: IRemittanceDetailResponse,
  wallets: WalletType[],
  purpose: string
): MovementModalDetailsType[] {
  const { beneficiary } = transactionData;

  const isStablecoin = beneficiary?.countryCode === STABLECOIN_COUNTRY_CODE;
  const isSwift = checkIfBeneficiaryHasSwiftRoute(beneficiary as unknown as Destinatary);
  const isPixTransferType = PIX_TYPES.some(
    (item) => item.value === beneficiary?.routingCodeType1
  );
  const showTooltip = transactionData.txStatus?.statusDB === 'DEVUELTO';

  const taxes = transactionData.transactionCostList || [];

  const hasOperationalCost = taxes.some(
    (item) => item.chargeType === 'COSTO OPERACIONAL'
  );

  if (!hasOperationalCost) {
    taxes.push({
      amount: 0,
      markup: 0,
      fixed: 0,
      chargeType: 'COSTO OPERACIONAL',
      productId: 0,
    });
  }

  const filteredTaxes = taxes
    .filter(
      (item) =>
        !(
          item.chargeType === 'SIN COSTO' ||
          item.chargeType === 'COSTO TIPO CAMBIO'
        )
    )
    .map((item) => {
      if (item.chargeType === 'COSTO OPERACIONAL') {
        return {
          label: textFeeQuoter(item?.chargeType),
          value: formatCurrency(item.amount, transactionData.originCurrency),
          show: true,
          ...(showTooltip && { ...buildTooltipDetail(transactionData, taxes) }),
        };
      }

      return {
        label: textFeeQuoter(item?.chargeType),
        value: formatCurrency(item.amount, transactionData.originCurrency),
        show: true,
      };
    });

  const mainWallet =
    wallets.find((wallet) => wallet.isMainAccount) || ({} as WalletType);
  const compensationAccountsCurrencies = wallets
    .filter((wallet) =>
      wallet.compensationNumbers.some((compensation) => compensation.active)
    )
    ?.map((compensationAccount) => compensationAccount.currency);

  const isFromCompensantionAccount = compensationAccountsCurrencies.includes(
    transactionData.originCurrency
  );

  const isCOP =
    mainWallet.currency === 'COP' &&
    (transactionData.originCurrency === 'COP' ||
      isFromCompensantionAccount ||
      transactionData.destinyCurrency === 'COP');

  const isInternational =
    isFromCompensantionAccount ||
    transactionData.originCurrency !== transactionData.destinyCurrency;

  const hasNumeralExchange = Boolean(
    transactionData.purposeOriginNumeral ||
      transactionData.purposeDestinyNumeral
  );

  const showNumeralExchange = isCOP && isInternational && hasNumeralExchange;

  const accountNumber =
    transactionData.beneficiary?.currencyCode === 'ARS'
      ? transactionData.beneficiary?.routingCodeValue1
      : transactionData.beneficiary?.accountNumber;

  const accountNameType = getAccountNameType(
    transactionData.beneficiary?.countryCode || '',
    transactionData.beneficiary?.accountType || ''
  );

  const accountBankName = transactionData.beneficiary?.accountBankName;

  const networkIconPath = isStablecoin
    ? BANK_NAME_ICON_MAP[transactionData.beneficiary?.accountBankName ?? '']
    : undefined;
  const currencyIconPath = isStablecoin
    ? STABLECOIN_CURRENCY_ICON_MAP[transactionData.destinyCurrency]
    : undefined;
  const rateBreakdown = getRateBreakdown({
    destinationAmount: transactionData.destinyAmount,
    destinationCurrency: transactionData.destinyCurrency,
    destinyRate: Number(transactionData.destinyRate ?? 0),
    fee: transactionData.fee,
    originAmount: transactionData.originAmount,
    originCurrency: transactionData.originCurrency,
    rate: transactionData.rate,
    destinyRateString: transactionData.destinyRateString,
  });

  return [
    {
      label: 'Destinatario',
      value: transactionData.beneficiary?.name,
      show: true,
    },
    {
      label: transactionData.beneficiary?.dniType,
      value: transactionData.beneficiary?.dni,
      show: true,
    },
    {
      label: 'Email',
      value: transactionData.beneficiary?.email,
      show: Boolean(transactionData.beneficiary?.email),
    },
    {
      label: 'País',
      value: transactionData.beneficiary?.countryName,
      show: !isStablecoin,
    },
    {
      label: 'Tipo de clave PIX',
      value: getPixType(beneficiary as unknown as Destinatary),
      show: isPixTransferType,
    },
    {
      label: 'Clave PIX',
      value: beneficiary?.routingCodeValue1 || '',
      show: isPixTransferType,
    },
    {
      label: isStablecoin ? 'Red' : 'Banco',
      value: accountBankName,
      show: !isSwift && !isPixTransferType && Boolean(accountBankName),
      ...(networkIconPath && { icon: getAsset(networkIconPath) }),
    },
    {
      label: 'Tipo de cuenta',
      value: accountNameType,
      show: !isStablecoin && !isSwift && !isPixTransferType && Boolean(accountNameType),
    },
    {
      label: 'Agencia',
      value: transactionData.beneficiary?.branchCode,
      show: Boolean(!isPixTransferType && transactionData.beneficiary?.branchCode),
    },
    {
      label: isStablecoin ? 'Dirección de wallet' : 'N° de cuenta',
      value: isStablecoin ? maskWalletAddress(accountNumber ?? '') : accountNumber,
      show: !isPixTransferType && Boolean(accountNumber),
    },
    {
      label: 'Hash de transacción',
      value: transactionData.additionalProviderInfo?.stablecoinHash ?? '',
      show: isStablecoin && Boolean(transactionData.additionalProviderInfo?.stablecoinHash),
      breakLine: true,
    },
    {
      type: 'divider',
      show: true,
    },
    {
      label: 'Tú enviaste',
      value: formatCurrency(
        String(transactionData.originAmount),
        transactionData.originCurrency
      ),
      show: true,
    },
    {
      label: 'Costo tipo de cambio',
      value: formatCurrency(
        String(transactionData.fee),
        transactionData.originCurrency
      ),
      show: true,
    },
    ...filteredTaxes,
    {
      label: 'Descuento',
      value: formatCurrency(
        String(transactionData.discountAmount),
        transactionData.originCurrency
      ),
      show: transactionData.discountAmount > 0,
    },
    {
      label: 'Tipo de cambio Global66',
      value: rateBreakdown.global66Formatted,
      show: Boolean(rateBreakdown.global66Formatted),
      visualLevel: 'highlight',
    },
    {
      label: 'Spot',
      value: rateBreakdown.spotFormatted,
      show: rateBreakdown.showBreakdown,
      visualLevel: 'nested',
    },
    {
      label: 'Spread',
      value: rateBreakdown.spreadFormatted,
      show: rateBreakdown.showBreakdown,
      visualLevel: 'nested',
    },
    {
      label: 'Destinatario recibirá',
      value: formatCurrency(
        String(transactionData.destinyAmount),
        transactionData.destinyCurrency
      ),
      show: true,
      ...(currencyIconPath && { icon: getAsset(currencyIconPath) }),
    },
    {
      type: 'divider',
      show: true,
    },
    {
      label: 'Comentario',
      value: transactionData.comment,
      show: true,
    },
    {
      label: 'Propósito',
      value: purpose || '',
      show: true,
    },
    {
      label: 'Numeral cambiario',
      value:
        transactionData.purposeOriginNumeral ||
        transactionData.purposeDestinyNumeral ||
        '',
      show: showNumeralExchange,
    },
    {
      label: 'Declaración cambiario',
      value: transactionData.exchangeDestinationNumeral || '',
      show: showNumeralExchange && Boolean(transactionData.exchangeDestinationNumeral),
    },
  ];
}

export function transferStatus(movement: NewTypeMovement, status: StatusDb) {
  const isVerifyPayment = status === 'ESPERANDO_PAGO' && movement?.voucher;

  const availableStatus = {
    ESPERANDO_PAGO: {
      ...STATUS_LIST.ESPERANDO_PAGO,
      ...(isVerifyPayment && { text: 'Verificando pago' }),
    },
    COTIZACION_VENCIDA: { ...STATUS_LIST.COTIZACION_VENCIDA },
    REVISANDO_PAGO: { ...STATUS_LIST.REVISANDO_PAGO },
    PAGO_CONFIRMADO: { ...STATUS_LIST.PAGO_CONFIRMADO },
    EN_REVISION: { ...STATUS_LIST.EN_REVISION },
    TRANSFERENCIA_ENVIADA: { ...STATUS_LIST.TRANSFERENCIA_ENVIADA },
    TRANSFERENCIA_EXITOSA: { ...STATUS_LIST.TRANSFERENCIA_EXITOSA },
    DEVOLUCION_EN_PROCESO: { ...STATUS_LIST.DEVOLUCION_EN_PROCESO },
    DEVUELTO: { ...STATUS_LIST.DEVUELTO },
    ENVIO_ANULADO: { ...STATUS_LIST.ENVIO_ANULADO },
    DATOS_VERIFICADOS: { ...STATUS_LIST.DATOS_VERIFICADOS },
    ENVIO_RECHAZADO: { ...STATUS_LIST.ENVIO_RECHAZADO },
    UNDER_COMPLIANCE_REVIEW: { ...STATUS_LIST.UNDER_COMPLIANCE_REVIEW },
  };

  if (Object.keys(availableStatus).includes(status)) {
    return availableStatus[status as keyof typeof availableStatus];
  }
  return false;
}
